// The reviver's third argument, `context.source`, is the raw JSON text
// for the current value. Older Firefox/Safari don't ship it, so the
// polyfill is required to recover full-precision digits below.
import "core-js/actual/json/parse";

// JSON keys whose values are nanosecond-epoch integers. The reviver allowlists
// by key name: a key listed here is re-read from its raw token as a `bigint`
// everywhere the reviver runs, and an integer under any other key stays the
// `Number` the default parse produced, already rounded if it ran past
// `Number.MAX_SAFE_INTEGER` (in ns-epoch, anything after ≈ April 15, 1970).
//
// - Topic and Event records: `start_time`, `end_time`.
// - Session aggregate bounds: `min_timestamp_ns`, `max_timestamp_ns`.
// - The session times a metric data point is placed at:
//   `session_start_time_ns`, `session_end_time_ns`.
// - The window of a file a session covers, both as the caller declares it (in
//   the file's own timestamps, `min_file_timestamp_ns` /
//   `max_file_timestamp_ns`, plus `anchor_ns`, the wall-clock instant that
//   data's time 0 sat at) and as the server reports it back
//   (`min_wall_clock_timestamp_ns` / `max_wall_clock_timestamp_ns`, plus the
//   `unix_epoch_offset_ns` it added to get there).
// - Magic-link context object: `t`.
export const NANOSECOND_TIMESTAMP_KEYS = new Set<string>([
  "start_time",
  "end_time",
  "min_timestamp_ns",
  "max_timestamp_ns",
  "session_start_time_ns",
  "session_end_time_ns",
  "min_file_timestamp_ns",
  "max_file_timestamp_ns",
  "min_wall_clock_timestamp_ns",
  "max_wall_clock_timestamp_ns",
  "anchor_ns",
  "unix_epoch_offset_ns",
  "t",
]);

/**
 * `JSON.parse` reviver that returns a `bigint` for every key in
 * `NANOSECOND_TIMESTAMP_KEYS`, re-read from the raw token text in
 * `context.source` so no digit is lost. Every other key, and any allowlisted
 * key whose token is not an integer, comes back exactly as the default parse
 * produced it.
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/parse#the_reviver_parameter
 * https://github.com/tc39/proposal-json-parse-with-source
 */
export function parseTimestampsAsBigInt(
  key: string,
  value: unknown,
  context?: { source: string },
) {
  // `context` is absent when the polyfill above failed to install on an engine
  // that lacks the source-text arg. `null` is the real value of an unset
  // bound, not a token to re-read.
  if (
    NANOSECOND_TIMESTAMP_KEYS.has(key) &&
    context !== undefined &&
    value !== null
  ) {
    try {
      return BigInt(context.source);
    } catch {
      // `BigInt` fails two ways here and both are swallowed. SyntaxError when
      // the token isn't an integer, most often because a customer's
      // user-metadata reuses one of these keys with a non-integer value.
      // TypeError when there is no token at all: `context.source` is undefined
      // for object and array values, so those land here too. Roboto's own
      // fields on these keys are always integers.
      return value;
    }
  }

  return value;
}
